// Import styles
import './style.scss';
import './editor.scss';

import { registerBlockType } from '@wordpress/blocks';
import { useBlockProps, InspectorControls, RichText } from '@wordpress/block-editor';
import { PanelBody, TextControl, SelectControl, BaseControl, TabPanel, ToggleControl } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { useSelect } from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';
import PostSearch from '../../components/PostSearch';
import ToggleSwitch from '../../components/ToggleSwitch';
import VisibilityControl from '../../components/VisibilityControl';
import FlatColorPicker from '../../components/FlatColorPicker';
import Typography from '../../components/Typography';
import Loading from '../../utils/Loading';
import ServerSideRender from '@wordpress/server-side-render';

registerBlockType('kanews/related-posts', {
    apiVersion: 2,
    title: __('İlgili Yazı', 'kanews'),
    icon: 'admin-links',
    category: 'kanews',
    supports: {
        html: false,
        align: ['wide', 'full']
    },
    attributes: {
        title: {
            type: 'string',
            default: __('İlginizi Çekebilir', 'kanews')
        },
        postCount: {
            type: 'number',
            default: 3
        },
        selectedPosts: {
            type: 'array',
            default: []
        },
        headingTag: {
            type: 'string',
            default: 'div'
        },
        detailText: {
            type: 'string',
            default: __('Haberi görüntüle', 'kanews')
        },
        showDetailText: {
            type: 'boolean',
            default: true
        },
        showTitle: {
            type: 'boolean',
            default: true
        },
        backgroundColor: {
            type: 'string',
            default: ''
        },
        visibility: {
            type: 'object',
            default: {
                desktop: false,
                tablet: false,
                mobile: false
            }
        },
        titleTypography: {
            type: 'object',
            default: {
                fontFamily: '',
                fontSize: '',
                fontWeight: 'normal',
                lineHeight: '',
                letterSpacing: '',
                textTransform: 'none',
                fontStyle: 'normal',
                textDecoration: 'none'
            }
        }
    },
    edit: function(props) {
        const { attributes, setAttributes } = props;
        const blockProps = useBlockProps();

        const { posts, isLoading } = useSelect((select) => {
            const selectedPosts = attributes.selectedPosts;
            const query = {
                include: selectedPosts,
                per_page: -1,
                _embed: true
            };

            return {
                posts: select(coreStore).getEntityRecords('postType', 'post', query),
                isLoading: select(coreStore).isResolving('getEntityRecords', ['postType', 'post', query])
            };
        }, [attributes.selectedPosts]);

        const tabs = [
            {
                name: 'content',
                title: __('İçerik', 'kanews'),
                className: 'content-tab'
            },
            {
                name: 'design',
                title: __('Tasarım', 'kanews'),
                className: 'design-tab'
            },
            {
                name: 'visibility',
                title: __('Görünürlük', 'kanews'),
                className: 'visibility-tab'
            }
        ];

        return (
            <div {...blockProps}>
                <InspectorControls>
                    <TabPanel
                        className="kanews-tab-panel"
                        tabs={tabs}
                    >
                        {(tab) => {
                            return (
                                <>
                                    {tab.name === 'content' && (
                                        <PanelBody>
                                            <ToggleControl
                                                label={__('Başlık Göster', 'kanews')}
                                                checked={attributes.showTitle}
                                                onChange={(value) => setAttributes({ showTitle: value })}
                                            />

                                            {attributes.showTitle && (
                                                <RichText
                                                    tagName="h2"
                                                    value={attributes.title}
                                                    onChange={(value) => setAttributes({ title: value })}
                                                    placeholder={__('Başlık...', 'kanews')}
                                                />
                                            )}

                                            <BaseControl
                                                label={__('Başlık Etiketi', 'kanews')}
                                                __nextHasNoMarginBottom={true}
                                            >
                                                <SelectControl
                                                    value={attributes.headingTag}
                                                    options={[
                                                        { label: 'H2', value: 'h2' },
                                                        { label: 'H3', value: 'h3' },
                                                        { label: 'H4', value: 'h4' },
                                                        { label: 'H5', value: 'h5' },
                                                        { label: 'H6', value: 'h6' },
                                                        { label: 'div', value: 'div' }
                                                    ]}
                                                    onChange={(value) => setAttributes({ headingTag: value })}
                                                    __nextHasNoMarginBottom={true}
                                                    __next40pxDefaultSize={true}
                                                />
                                            </BaseControl>

                                            <BaseControl
                                                __nextHasNoMarginBottom={true}
                                            >
                                                <PostSearch
                                                    selectedPosts={attributes.selectedPosts}
                                                    multiple={false}
                                                    labelText={__('Gösterilecek Yazı', 'kanews')}
                                                    onChange={(selectedPosts) => {
                                                        console.log('Selected posts:', selectedPosts);
                                                        setAttributes({ selectedPosts: selectedPosts.map(post => post.id) });
                                                    }}
                                                />
                                            </BaseControl>

                                            <ToggleSwitch
                                                label={__('Devamını Oku', 'kanews')}
                                                checked={attributes.showDetailText}
                                                onChange={(value) => setAttributes({ showDetailText: value })}
                                            />

                                            {attributes.showDetailText && (
                                                <BaseControl
                                                    __nextHasNoMarginBottom={true}
                                                >
                                                    <TextControl
                                                        value={attributes.detailText}
                                                        onChange={(value) => setAttributes({ detailText: value })}
                                                        __nextHasNoMarginBottom={true}
                                                        __next40pxDefaultSize={true}
                                                    />
                                                </BaseControl>
                                            )}
                                            
                                        </PanelBody>
                                    )}

                                    {tab.name === 'design' && (
                                        <PanelBody>
                                            <Typography
                                                label={__('Yazı Başlığı Tipografisi', 'kanews')}
                                                value={attributes.titleTypography}
                                                onChange={(value) => setAttributes({ titleTypography: value })}
                                            />
                                            <FlatColorPicker
                                                label={__('Arkaplan Rengi', 'kanews')}
                                                value={attributes.backgroundColor}
                                                onChange={(value) => setAttributes({ backgroundColor: value })}
                                            />
                                        </PanelBody>
                                    )}

                                    {tab.name === 'visibility' && (
                                        <PanelBody>
                                            <VisibilityControl
                                                value={attributes.visibility}
                                                onChange={(value) => setAttributes({ visibility: value })}
                                            />
                                        </PanelBody>
                                    )}
                                </>
                            );
                        }}
                    </TabPanel>
                </InspectorControls>
        
                <ServerSideRender
                    block="kanews/related-posts"
                    attributes={attributes}
                    LoadingResponsePlaceholder={() => (
                        <Loading text={__('İlgili yazılar yükleniyor...', 'kanews')} />
                    )}
                    httpMethod="GET"
                />
                
            </div>
        );
    },
    save: function() {
        return null;
    }
}); 